import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import {
  allAvailableFormats,
  availableAudioFormats,
  availableImageFormats,
  availableVideoFormats,
  BlobFilePreview,
  MediaBlobType
} from '@myme/utils';
import { webPalette } from 'src/themes';
import { usePalette, useTranslation } from 'src/hooks';
import { Button, Typography } from 'src/components/atoms';
import { Flex } from 'src/components/layout';
import { useMediaContext } from './MediaContext';
import { useUploadNewMediaFile } from './useUploadNewMediaFile';

interface LibraryProps {
  type: 'LIBRARY';
  media: BlobFilePreview[];
  mediaType: 'ALL';
  isLoading: boolean;
}

interface EditorProps {
  type: 'EDITOR';
  media: BlobFilePreview[];
  mediaType: MediaBlobType;
  isLoading: boolean;
  onInsertAction: () => void;
}

type Props = LibraryProps | EditorProps;

const getAvailableExtensionsBasedOnMediaType = (mediaType: MediaBlobType | 'ALL') => {
  switch (mediaType) {
    case 'IMAGE': {
      return availableImageFormats;
    }
    case 'VIDEO': {
      return availableVideoFormats;
    }
    case 'AUDIO': {
      return availableAudioFormats;
    }
    case 'ALL':
    default: {
      return allAvailableFormats;
    }
  }
};

const StyledLabel = styled.label`
  cursor: pointer;
  padding: 0.375rem 1rem;
`;

const StyledUploadInput = styled.input`
  display: none;
`;

const uploadingAnimation = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const StyledUploadingInfo = styled(Typography)`
  animation: ${uploadingAnimation} 4s ease infinite;
  margin-right: 1rem;
`;

export const MediaContainerActions = ({ media, mediaType, isLoading, ...props }: Props) => {
  const { t } = useTranslation();
  const { error, primary } = usePalette();
  const { fileUUID, handleDeleteFile } = useMediaContext();
  const { handleUploadNewMediaFileMutation } = useUploadNewMediaFile(mediaType);

  return (
    <Flex justifyContent='space-between' mt={4} mb={-2}>
      {props.type === 'LIBRARY' ? (
        <Button
          disabled={!fileUUID || handleUploadNewMediaFileMutation.isLoading}
          staticWidth='9rem'
          onClick={handleDeleteFile}
          color='error'
          sx={{ backgroundColor: error.main }}
        >
          {t('pages.mymes.editor.blobs.library.media_delete_btn')}
        </Button>
      ) : (
        <div />
      )}
      <Flex>
        {handleUploadNewMediaFileMutation.isLoading && (
          <StyledUploadingInfo variant='body2'>
            {t('pages.mymes.editor.blobs.library.media_upload_loading_text')}
          </StyledUploadingInfo>
        )}
        <StyledUploadInput
          type='file'
          id='upload-file-button'
          onChange={e => handleUploadNewMediaFileMutation.mutate(e)}
          // Reset input value to have an option of choosing the same file again
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={(e: any) => {
            e.target.value = '';
          }}
          accept={getAvailableExtensionsBasedOnMediaType(mediaType)}
        />
        <Button
          disabled={isLoading || handleUploadNewMediaFileMutation.isLoading}
          loading={handleUploadNewMediaFileMutation.isLoading}
          staticWidth='9rem'
          color='secondary'
          sx={{ backgroundColor: webPalette.layoutUserPopover, p: 0 }}
        >
          <StyledLabel htmlFor='upload-file-button'>
            {t('pages.mymes.editor.blobs.library.media_upload_btn')}
          </StyledLabel>
        </Button>
        {props.type === 'EDITOR' && (
          <Button
            disabled={!fileUUID || handleUploadNewMediaFileMutation.isLoading}
            staticWidth='9rem'
            onClick={props.onInsertAction}
            color='primary'
            sx={{ backgroundColor: primary.main, ml: 2 }}
          >
            {t('pages.mymes.editor.blobs.library.media_insert_btn')}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
